import { useEffect, useRef, useState } from "react";
import "./OwlShooterGame.css";

// Read-only word collection for on-screen targets — never mutates the DOM,
// only reads .textContent once at mount. Excludes the game overlay itself
// (data-owl-game-overlay) and non-visible/script/style nodes.
const WORD_RE = /^[A-Za-z֐-׿]{2,14}$/;
const FALLBACK_WORDS = ["Prodigy", "תמיכה", "קריאה", "מייל", "טכנאי", "מערכת"];

function collectScreenWords(limit = 24) {
  const seen = new Set();
  const words = [];
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const text = node.textContent;
      if (!text || !text.trim()) return NodeFilter.FILTER_REJECT;
      const parent = node.parentElement;
      if (!parent) return NodeFilter.FILTER_REJECT;
      if (parent.closest("[data-owl-game-overlay], script, style, noscript")) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  let node;
  while ((node = walker.nextNode()) && words.length < limit * 4) {
    for (const raw of node.textContent.split(/\s+/)) {
      const cleaned = raw.replace(/[^A-Za-z֐-׿]/g, "");
      if (WORD_RE.test(cleaned) && !seen.has(cleaned)) {
        seen.add(cleaned);
        words.push(cleaned);
      }
    }
  }
  for (let i = words.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [words[i], words[j]] = [words[j], words[i]];
  }
  const picked = words.slice(0, limit);
  return picked.length >= 5 ? picked : FALLBACK_WORDS;
}

const MOVE_KEYS = new Set(["ArrowLeft", "ArrowRight", "a", "A", "d", "D"]);
const SHOOT_KEYS = new Set([" ", "Spacebar"]);
const OWL_Y_OFFSET = 60;
const OWL_SPEED = 7;
const SHOT_COOLDOWN_MS = 220;

/**
 * Purely visual, frontend-only overlay: a canvas mini-game drawn on top of
 * the current screen. Reads on-screen words once (read-only) to use as
 * targets; never mutates the underlying DOM and never calls any API.
 */
export default function OwlShooterGame({ onClose }) {
  const canvasRef = useRef(null);
  const [score, setScore] = useState(0);
  // Layout.jsx passes a fresh onClose closure on every render (it isn't memoized there). Reading
  // it through a ref, instead of listing it as an effect dependency, keeps the game (score,
  // targets, the animation loop) from resetting every time the parent re-renders while this is open.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const previousActiveElement = document.activeElement;
    const previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    if (previousActiveElement && typeof previousActiveElement.blur === "function") {
      previousActiveElement.blur();
    }

    const words = collectScreenWords();
    const keys = {};
    let owlX = 0;
    let projectiles = [];
    let targets = [];
    let explosions = [];
    let lastShot = 0;
    let scoreValue = 0;
    let rafId = null;

    const spawnTarget = (initial) => ({
      word: words[Math.floor(Math.random() * words.length)],
      x: Math.random() * Math.max(1, canvas.width - 140) + 20,
      y: initial ? Math.random() * Math.max(1, canvas.height - 220) + 40 : -20,
      speed: 0.4 + Math.random() * 0.7,
    });

    function resize() {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      owlX = canvas.width / 2;
    }
    resize();
    targets = Array.from({ length: 12 }, () => spawnTarget(true));

    function handleKeyDown(e) {
      if (e.key === "Escape") {
        onCloseRef.current();
        return;
      }
      if (MOVE_KEYS.has(e.key) || SHOOT_KEYS.has(e.key)) e.preventDefault();
      keys[e.key] = true;
      if (SHOOT_KEYS.has(e.key)) {
        const now = performance.now();
        if (now - lastShot > SHOT_COOLDOWN_MS) {
          lastShot = now;
          projectiles.push({ x: owlX, y: canvas.height - OWL_Y_OFFSET - 12 });
        }
      }
    }
    function handleKeyUp(e) {
      keys[e.key] = false;
    }

    window.addEventListener("resize", resize);
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    function loop() {
      const w = canvas.width;
      const h = canvas.height;
      ctx.clearRect(0, 0, w, h);

      if (keys["ArrowLeft"] || keys["a"] || keys["A"]) owlX -= OWL_SPEED;
      if (keys["ArrowRight"] || keys["d"] || keys["D"]) owlX += OWL_SPEED;
      owlX = Math.max(24, Math.min(w - 24, owlX));

      projectiles = projectiles.map((p) => ({ ...p, y: p.y - 9 })).filter((p) => p.y > -20);

      targets.forEach((t) => {
        t.y += t.speed;
        if (t.y > h + 20) {
          t.y = -20;
          t.x = Math.random() * Math.max(1, w - 140) + 20;
        }
      });

      ctx.font = "22px Arial, sans-serif";
      const survivors = [];
      for (const p of projectiles) {
        let hit = false;
        for (const t of targets) {
          const tw = ctx.measureText(t.word).width;
          if (p.x > t.x - 10 && p.x < t.x + tw + 10 && p.y > t.y - 26 && p.y < t.y + 10) {
            hit = true;
            scoreValue += 10;
            setScore(scoreValue);
            explosions.push({ x: t.x + tw / 2, y: t.y, r: 4, alpha: 1 });
            t.y = -30 - Math.random() * 150;
            t.x = Math.random() * Math.max(1, w - 140) + 20;
            t.word = words[Math.floor(Math.random() * words.length)];
            break;
          }
        }
        if (!hit) survivors.push(p);
      }
      projectiles = survivors;

      explosions = explosions.map((e) => ({ ...e, r: e.r + 3, alpha: e.alpha - 0.06 })).filter((e) => e.alpha > 0);

      ctx.textAlign = "left";
      ctx.textBaseline = "alphabetic";
      targets.forEach((t) => {
        ctx.lineWidth = 3;
        ctx.strokeStyle = "rgba(15,23,42,0.65)";
        ctx.strokeText(t.word, t.x, t.y);
        ctx.fillStyle = "#fbbf24";
        ctx.fillText(t.word, t.x, t.y);
      });

      ctx.fillStyle = "#38bdf8";
      projectiles.forEach((p) => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, 4, 0, Math.PI * 2);
        ctx.fill();
      });

      explosions.forEach((e) => {
        ctx.beginPath();
        ctx.fillStyle = `rgba(251,146,60,${Math.max(e.alpha, 0)})`;
        ctx.arc(e.x, e.y, e.r, 0, Math.PI * 2);
        ctx.fill();
      });

      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.font = "44px serif";
      ctx.fillText("🦉", owlX, h - OWL_Y_OFFSET);

      rafId = requestAnimationFrame(loop);
    }
    rafId = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", resize);
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      document.body.style.overflow = previousBodyOverflow;
    };
  }, []);

  return (
    <div className="owl-game-overlay" data-owl-game-overlay="true">
      <div className="owl-game-topbar">
        <span className="owl-game-score">ניקוד: {score}</span>
        <button type="button" className="owl-game-exit" onClick={onClose}>
          ✕ יציאה
        </button>
      </div>
      <canvas ref={canvasRef} className="owl-game-canvas" />
      <div className="owl-game-hint">← → / A D לתנועה &nbsp;|&nbsp; רווח לירי &nbsp;|&nbsp; Esc ליציאה</div>
    </div>
  );
}
